import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { MoreVertical, Trash2, Eye, Filter, ShoppingBag, Save, Settings } from 'lucide-react';

const statuses = [
  { value: 'nouveau', label: 'Nouveau', color: 'bg-gray-100 text-gray-700' },
  { value: 'paye', label: 'Payé', color: 'bg-blue-100 text-blue-800' },
  { value: 'en_preparation', label: 'En préparation', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'pret', label: 'Prêt', color: 'bg-green-100 text-green-800' },
  { value: 'recupere', label: 'Récupéré', color: 'bg-garden-100 text-garden-800' },
  { value: 'annule', label: 'Annulé', color: 'bg-red-100 text-red-800' },
];

export default function OrderManagement() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [ccSettings, setCcSettings] = useState({});
  const [savingCC, setSavingCC] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');

  const load = async () => {
    try {
      const [o, cc] = await Promise.all([adminApi.getOrders(), adminApi.getCCSettings()]);
      setOrders(o.data);
      setCcSettings(cc.data || {});
    } catch { toast.error('Erreur de chargement'); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.updateOrder(id, { status });
      load();
      toast.success('Statut mis à jour');
    } catch { toast.error('Erreur'); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    try { await adminApi.deleteOrder(id); load(); toast.success('Supprimé'); } catch { toast.error('Erreur'); }
  };

  const saveCCSettings = async () => {
    setSavingCC(true);
    try {
      await adminApi.updateCCSettings(ccSettings);
      toast.success('Paramètres Click & Collect sauvegardés');
    } catch { toast.error('Erreur'); }
    setSavingCC(false);
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);
  const getStatusInfo = (s) => statuses.find(st => st.value === s) || statuses[0];

  const activeOrders = orders.filter(o => ['paye', 'en_preparation'].includes(o.status));

  return (
    <AdminLayout title="Commandes Click & Collect">
      <div data-testid="admin-order-management">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-garden-100 mb-6">
            <TabsTrigger value="orders" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white">
              Commandes {activeOrders.length > 0 && <span className="ml-1 bg-sunny text-charcoal text-xs rounded-full px-1.5">{activeOrders.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-garden-800 data-[state=active]:text-white">
              <Settings size={14} className="mr-1" /> Configuration
            </TabsTrigger>
          </TabsList>

          {/* ORDERS TAB */}
          <TabsContent value="orders">
            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <Filter size={16} className="text-charcoal/50" />
              <div className="flex gap-2 overflow-x-auto">
                {[{ value: 'all', label: 'Toutes' }, ...statuses].map(s => (
                  <button key={s.value} onClick={() => setFilter(s.value)} data-testid={`admin-order-filter-${s.value}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${filter === s.value ? 'bg-garden-800 text-white' : 'bg-white text-charcoal border border-garden-200 hover:bg-garden-50'}`}>
                    {s.label} {s.value !== 'all' && <span className="ml-1">({orders.filter(o => o.status === s.value).length})</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-garden-200 overflow-hidden">
              {filtered.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="bg-garden-50">
                    <tr>
                      <th className="text-left p-3 font-medium text-garden-800">ID</th>
                      <th className="text-left p-3 font-medium text-garden-800">Client</th>
                      <th className="text-left p-3 font-medium text-garden-800 hidden sm:table-cell">Retrait</th>
                      <th className="text-left p-3 font-medium text-garden-800 hidden md:table-cell">Total</th>
                      <th className="text-left p-3 font-medium text-garden-800">Statut</th>
                      <th className="text-right p-3 font-medium text-garden-800">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((order) => {
                      const status = getStatusInfo(order.status);
                      return (
                        <tr key={order.id} className="border-t border-garden-100 hover:bg-garden-50/50" data-testid={`admin-order-row-${order.id}`}>
                          <td className="p-3 font-mono text-xs text-charcoal/60">{order.id}</td>
                          <td className="p-3">
                            <span className="font-medium text-charcoal">{order.customer_name}</span>
                            <p className="text-xs text-charcoal/50">{order.customer_phone}</p>
                          </td>
                          <td className="p-3 text-charcoal/70 hidden sm:table-cell">{order.pickup_date} à {order.pickup_time}</td>
                          <td className="p-3 font-medium text-garden-800 hidden md:table-cell">{order.total?.toFixed(2)} €</td>
                          <td className="p-3">
                            <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
                              <SelectTrigger className="w-36 h-8 text-xs border-0 p-0" data-testid={`admin-order-status-${order.id}`}>
                                <Badge className={`${status.color} border-0 text-xs`}>{status.label}</Badge>
                              </SelectTrigger>
                              <SelectContent className="bg-white">
                                {statuses.map(s => <SelectItem key={s.value} value={s.value}><Badge className={`${s.color} border-0 text-xs`}>{s.label}</Badge></SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger className="p-1 hover:bg-garden-100 rounded">
                                <MoreVertical size={16} className="text-charcoal/50" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-white">
                                <DropdownMenuItem onClick={() => setSelected(order)} className="text-xs"><Eye size={12} className="mr-2" /> Détails</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => deleteOrder(order.id)} className="text-xs text-red-500"><Trash2 size={12} className="mr-2" /> Supprimer</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <ShoppingBag size={32} className="mx-auto text-charcoal/20 mb-2" />
                  <p className="text-charcoal/50 text-sm">Aucune commande</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="bg-white rounded-xl border border-garden-200 p-6 max-w-xl space-y-5">
              <h2 className="font-heading font-semibold text-lg text-garden-800 mb-2">Configuration Click & Collect</h2>
              <div className="flex items-center gap-3">
                <Switch checked={ccSettings.enabled || false} onCheckedChange={v => setCcSettings({...ccSettings, enabled: v})} data-testid="cc-enabled-toggle" />
                <Label className="text-sm font-medium">Activer le Click & Collect</Label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Durée d'un créneau (min)</Label>
                  <Input type="number" value={ccSettings.slot_duration_minutes || 15} onChange={e => setCcSettings({...ccSettings, slot_duration_minutes: parseInt(e.target.value)})} className="border-garden-200 rounded-xl" data-testid="cc-slot-duration" />
                </div>
                <div>
                  <Label className="text-sm">Max commandes par créneau</Label>
                  <Input type="number" value={ccSettings.max_orders_per_slot || 5} onChange={e => setCcSettings({...ccSettings, max_orders_per_slot: parseInt(e.target.value)})} className="border-garden-200 rounded-xl" data-testid="cc-max-orders" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Temps de préparation (min)</Label>
                  <Input type="number" value={ccSettings.preparation_time_minutes || 20} onChange={e => setCcSettings({...ccSettings, preparation_time_minutes: parseInt(e.target.value)})} className="border-garden-200 rounded-xl" data-testid="cc-prep-time" />
                </div>
                <div>
                  <Label className="text-sm">Montant minimum (€)</Label>
                  <Input type="number" step="0.5" value={ccSettings.min_order_amount || 5} onChange={e => setCcSettings({...ccSettings, min_order_amount: parseFloat(e.target.value)})} className="border-garden-200 rounded-xl" data-testid="cc-min-amount" />
                </div>
              </div>
              <Button onClick={saveCCSettings} disabled={savingCC} data-testid="cc-save-settings-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full">
                <Save size={16} className="mr-1" /> {savingCC ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Detail panel */}
        {selected && (
          <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelected(null)}>
            <div className="bg-white w-full max-w-md p-6 overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="order-detail-panel">
              <h2 className="font-heading font-semibold text-xl text-garden-800 mb-4">Commande {selected.id}</h2>
              <div className="space-y-3 text-sm">
                <div><span className="text-charcoal/50">Client:</span> <span className="font-medium">{selected.customer_name}</span></div>
                <div><span className="text-charcoal/50">Email:</span> <span>{selected.customer_email}</span></div>
                <div><span className="text-charcoal/50">Téléphone:</span> <a href={`tel:${selected.customer_phone}`} className="text-garden-600">{selected.customer_phone}</a></div>
                <div><span className="text-charcoal/50">Retrait:</span> <span>{selected.pickup_date} à {selected.pickup_time}</span></div>
                <div><span className="text-charcoal/50">Total:</span> <span className="font-bold text-garden-800">{selected.total?.toFixed(2)} €</span></div>
                <div><span className="text-charcoal/50">Paiement:</span> <Badge className={selected.payment_status === 'paid' ? 'bg-green-100 text-green-800 border-0' : 'bg-yellow-100 text-yellow-800 border-0'}>{selected.payment_status}</Badge></div>
                {selected.notes && <div><span className="text-charcoal/50">Notes:</span> <span>{selected.notes}</span></div>}
                <div className="border-t border-garden-200 pt-3">
                  <span className="text-charcoal/50 block mb-2">Articles:</span>
                  {selected.items?.map((item, i) => (
                    <div key={i} className="flex justify-between py-1">
                      <span>{item.quantity}x {item.name}{item.variant_name ? ` (${item.variant_name})` : ''}</span>
                      <span className="font-medium">{item.subtotal?.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="mt-6 text-sm text-garden-600 hover:text-garden-800">Fermer</button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
